import { useEffect, useMemo, useState } from 'react';
import type { FragmentManifest } from '@braidlabs/gateway';
import { call } from '@/api.ts';
import type { FragmentDraft, HeadState } from '@/api.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx';
import { useAuthenticated } from '@/session.tsx';

/**
 * The fragments list — the resource view this console was missing.
 *
 * **This shows what is live, not what is drafted**, and the distinction is the whole reason this
 * page needs care. Three things in this system are called some variant of "the registry":
 *
 * 1. the **published** snapshot that `CHAN#live` points at — the only one serving traffic;
 * 2. our **server-side drafts** (`GET /fragments`), written by `PUT /fragments/{id}`;
 * 3. the **editor's draft**, which lives in the browser and never touches the server until
 *    publish — `@braidlabs/console` is explicit that "a draft lives in whatever is editing it".
 *
 * All three can disagree at once. A table that simply listed `GET /fragments` would confidently
 * show fragments that compose nothing, which is worse than showing nothing at all. So the rows
 * are the published manifests, and the drafts are read only to *annotate* them: modified, or
 * staged and not yet live.
 */
type Status = 'live' | 'modified' | 'staged' | 'removed';

interface Row {
  id: string;
  status: Status;
  endpoint: string;
  pierce: string[];
  bound: boolean;
  adapter: string;
  src?: string;
}

const STATUS_META: Record<Status, { label: string; variant: 'success' | 'warning' | 'secondary' | 'destructive'; hint: string }> = {
  live: { label: 'Live', variant: 'success', hint: 'Published and serving traffic.' },
  modified: { label: 'Modified', variant: 'warning', hint: 'The saved draft differs from what is published. Publish to apply it.' },
  staged: { label: 'Staged', variant: 'secondary', hint: 'Saved as a draft but never published — this fragment composes nothing yet.' },
  removed: { label: 'Removed', variant: 'destructive', hint: 'Published, but the draft no longer contains it. Publishing will remove it.' },
};

type SortKey = 'id' | 'status' | 'endpoint';

export function FragmentsPage() {
  const session = useAuthenticated();
  const [head, setHead] = useState<HeadState | null>(null);
  const [drafts, setDrafts] = useState<FragmentDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'id', desc: false });

  useEffect(() => {
    let live = true;
    setError(null);
    Promise.all([
      call<HeadState>(session, '/registry/head'),
      call<{ items: FragmentDraft[] }>(session, '/fragments'),
    ])
      .then(([headState, draftList]) => {
        if (!live) return;
        setHead(headState);
        setDrafts(draftList.items);
      })
      .catch((cause: Error) => { if (live) setError(cause.message); });
    return () => { live = false; };
  }, [session]);

  const rows = useMemo(() => {
    if (!head || !drafts) return null;
    return buildRows(head.snapshot?.manifests ?? [], drafts);
  }, [head, drafts]);

  const visible = useMemo(() => {
    if (!rows) return null;
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? rows.filter((row) =>
          row.id.toLowerCase().includes(needle) ||
          row.endpoint.toLowerCase().includes(needle) ||
          row.pierce.some((pattern) => pattern.toLowerCase().includes(needle)))
      : rows;

    return [...filtered].sort((a, b) => {
      const compared = String(a[sort.key]).localeCompare(String(b[sort.key]));
      return sort.desc ? -compared : compared;
    });
  }, [rows, query, sort]);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load the registry</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const pending = rows?.filter((row) => row.status !== 'live').length ?? 0;

  return (
    <section className="grid gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[34px] leading-tight font-semibold tracking-[-0.02em]">Fragments</h1>
          <p className="text-muted-foreground text-sm">
            {head?.id ? (
              <>
                Serving from <code className="font-mono text-xs">{head.id}</code>
              </>
            ) : (
              'Nothing published yet — no fragment is composing traffic.'
            )}
          </p>
        </div>

        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by id, endpoint, or pattern"
          aria-label="Filter fragments"
          className="w-[300px] max-w-full"
        />
      </header>

      {/* The warning strip. A left border in process yellow rather than a bordered box: the page
          is an open broadsheet, and a card here would read as a dialog. */}
      {pending > 0 && (
        <Alert variant="warning" className="border-warning rounded-none border-0 border-l-[3px] px-3.5 py-3">
          <AlertTitle>
            {pending} {pending === 1 ? 'fragment differs' : 'fragments differ'} from what is live
          </AlertTitle>
          <AlertDescription className="text-[13.5px]">
            Saved drafts do not compose anything until they are published. Publish from the
            Registry tab.
          </AlertDescription>
        </Alert>
      )}

      <div>
        {visible === null ? (
          <TableSkeleton />
        ) : visible.length === 0 ? (
          <EmptyState
            title={query ? 'No fragments match that filter' : 'No fragments yet'}
            description={
              query
                ? 'Try a shorter search, or clear the filter.'
                : 'Add a fragment in the Registry tab, then publish it to start composing.'
            }
          />
        ) : (
          <Table>
            {/* Column widths are fixed so the four status chips cannot shift the columns beside
                them as rows change state. */}
            <colgroup>
              <col className="w-[180px]" />
              <col className="w-[130px]" />
              <col />
              <col className="w-[270px]" />
              <col className="w-[190px]" />
            </colgroup>
            <TableHeader className="[&_tr]:border-foreground [&_tr]:border-b">
              <TableRow>
                <SortableHead label="Fragment" sortKey="id" sort={sort} onSort={setSort} />
                <SortableHead label="Status" sortKey="status" sort={sort} onSort={setSort} />
                <SortableHead label="Endpoint" sortKey="endpoint" sort={sort} onSort={setSort} />
                <TableHead>Pierces</TableHead>
                <TableHead>Kind</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((row) => {
                const meta = STATUS_META[row.status];
                return (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-[13px] font-semibold">{row.id}</TableCell>
                    <TableCell>
                      <Badge variant={meta.variant} title={meta.hint}>
                        {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground truncate font-mono text-xs" title={row.endpoint}>
                      {row.endpoint}
                    </TableCell>
                    <TableCell>
                      {row.pierce.length === 0 ? (
                        <span className="text-muted-foreground text-xs">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {row.pierce.map((pattern) => (
                            <code key={pattern} className="font-mono text-xs">
                              {pattern}
                            </code>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-[13px]">
                        {row.bound ? 'Screen' : 'Widget'}
                        {row.src && <span className="text-muted-foreground font-mono"> {row.src}</span>}
                      </span>
                      <span className="text-muted-foreground block text-xs">{row.adapter}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  );
}

function SortableHead({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; desc: boolean };
  onSort(next: { key: SortKey; desc: boolean }): void;
}) {
  const active = sort.key === sortKey;
  return (
    <TableHead>
      <button
        type="button"
        className="hover:text-foreground inline-flex items-center gap-1 uppercase"
        onClick={() => onSort({ key: sortKey, desc: active ? !sort.desc : false })}
        aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}
      >
        {label}
        {/* These designs carry no icon set, so the sort marker is a glyph. It is hidden from
            assistive tech because `aria-sort` above already says the same thing, better. */}
        <span aria-hidden="true" className={active ? 'text-primary' : 'opacity-0'}>
          {active && sort.desc ? '↓' : '↑'}
        </span>
      </button>
    </TableHead>
  );
}

function TableSkeleton() {
  return (
    <div className="grid gap-3 p-4">
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex items-center gap-4">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

/**
 * Reconciles the published manifests against the saved drafts.
 *
 * Published rows come first-class; drafts only decide the status column. Comparison is by
 * serialized manifest, which is the same notion of equality the content-addressed snapshot id
 * uses — two manifests that serialize identically *are* the same configuration.
 */
export function buildRows(published: FragmentManifest[], drafts: FragmentDraft[]): Row[] {
  const draftById = new Map(drafts.map((draft) => [draft.fragmentId, draft.manifest]));
  const rows: Row[] = [];

  for (const manifest of published) {
    const draft = draftById.get(manifest.id);
    const status: Status = !draft
      ? 'removed'
      : stableEqual(draft, manifest)
        ? 'live'
        : 'modified';
    rows.push(toRow(manifest, status));
    draftById.delete(manifest.id);
  }

  // Whatever is left is drafted but never published — it composes nothing today.
  for (const [, manifest] of draftById) rows.push(toRow(manifest, 'staged'));

  return rows;
}

function toRow(manifest: FragmentManifest, status: Status): Row {
  return {
    id: manifest.id,
    status,
    endpoint: typeof manifest.endpoint === 'string' ? manifest.endpoint : '[fetcher function]',
    pierce: manifest.pierce ?? [],
    bound: manifest.bound ?? true,
    adapter: manifest.adapter ?? 'compat',
    ...(manifest.src ? { src: manifest.src } : {}),
  };
}

/** Key-order-independent equality, matching how a snapshot id is derived. */
function stableEqual(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([left], [right]) => left.localeCompare(right));
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`).join(',')}}`;
}
